import type { HabitCategory } from '../core/types.js';

export interface SafetyNote {
  level: 'info' | 'caution' | 'serious';
  short: string;
  long: string;
  resources?: string[];
}

const NONE: SafetyNote = {
  level: 'info',
  short: '',
  long: '',
};

const HIGH_RISK_ALCOHOL: SafetyNote = {
  level: 'serious',
  short: 'Heavy daily drinking can be unsafe to stop suddenly.',
  long: 'If you drink heavily and notice withdrawal symptoms (shaking, sweating, anxiety, hallucinations, seizures), please talk to a doctor before reducing aggressively. Wean is a pacing tool, not a medical detox.',
  resources: [
    'In the US, SAMHSA helpline: 1-800-662-4357',
    'In the UK, Drinkline: 0300 123 1110',
  ],
};

const HIGH_RISK_TOBACCO: SafetyNote = {
  level: 'caution',
  short: 'Cravings can be intense in the first days. Be kind to yourself.',
  long: 'Nicotine withdrawal is uncomfortable but rarely medically dangerous. If you are pregnant, have heart disease, or take prescription medication, ask a clinician how reduction may interact with your treatment.',
};

const HIGH_RISK_GAMBLING: SafetyNote = {
  level: 'serious',
  short: 'If gambling is causing financial or relationship harm, support helps.',
  long: 'Quota plans alone may not be enough for problem gambling. Consider speaking with a counselor or a peer-support service.',
  resources: [
    'In the US, National Council on Problem Gambling: 1-800-GAMBLER',
    'In the UK, GamCare: 0808 8020 133',
  ],
};

const HIGH_RISK_CANNABIS: SafetyNote = {
  level: 'caution',
  short: 'Sleep and mood may be rougher in the first week of cutting back.',
  long: 'Heavy long-term cannabis use can cause withdrawal symptoms (poor sleep, irritability, low appetite). They usually peak around days 2–6 and ease within two weeks.',
};

const HIGH_RISK_FOOD: SafetyNote = {
  level: 'caution',
  short: 'Food is fuel, not the enemy. Tread gently.',
  long: 'Restricting, purging, or binge–restrict cycles are signs to pause this kind of tracking and talk to someone. Wean is not designed to support eating-disorder recovery; use a clinician-led program. Number-tracking can make things worse if intake is already low or inconsistent.',
  resources: [
    'NEDA Helpline (US): 1-800-931-2237',
    'Beat Eating Disorders (UK): 0808 801 0677',
  ],
};

const SOFT_NOTE: SafetyNote = {
  level: 'info',
  short: 'Wean is a pacing tool, not a clinician.',
  long: 'If reducing this habit ever feels unsafe — physically or emotionally — please talk to a professional or a trusted person.',
};

export function safetyFor(category: HabitCategory): SafetyNote {
  switch (category) {
    case 'alcohol': return HIGH_RISK_ALCOHOL;
    case 'smoking': case 'vaping': return HIGH_RISK_TOBACCO;
    case 'gambling': return HIGH_RISK_GAMBLING;
    case 'cannabis': return HIGH_RISK_CANNABIS;
    case 'snacking': return HIGH_RISK_FOOD;
    case 'porn': case 'shopping': case 'gaming': case 'social_media': return SOFT_NOTE;
    case 'caffeine': case 'other': default: return NONE;
  }
}
